import { DirectionFindDto, type DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import { UnitFindDto, type UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import { FilialFindDto, type FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import { DealFindDto, type DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import { CompanyFindDto, type CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import { ContragentFindDto, type ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import { Connection1cFindDto, type Connection1cFindInterface } from '@zaoblako/shared/modules/integration/dtos/connection-1c/connection-1c-find.dto';
import { BillPaymentStatusEnum } from '@zaoblako/shared/modules/document/enums/bill.payment.status.enum';
import { BillShipmentStatusEnum } from '@zaoblako/shared/modules/document/enums/bill.shipment.status.enum';
import { MailingAttachmentStatusEnum } from '@zaoblako/shared/modules/notification/enums/mailing-attachment-status.enum';
import { UserFindDto, type UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { NdsFindDto, type NdsFindInterface } from '@zaoblako/shared/modules/finance/dtos/nds-find.dto';
import { CurrencyFindDto, type CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';
import { ContractFindDto, type ContractFindInterface } from '@zaoblako/shared/modules/document/dtos/contract/contract-find.dto';
import { FileFindDto, type FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file/file-find.dto';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { DocumentDtoKeysEnum } from '@zaoblako/shared/modules/document/enums/document-dto-keys.enum';
import { TaskDtoKeysEnum } from '@zaoblako/shared/modules/task/enum/task-dto-keys.enum';
import type { PlannedIncomeFindInterface } from '@zaoblako/shared/modules/task/dtos/planned-income/planned-income-find.dto';

import * as yup from 'yup';

/** Bill response body */
export interface BillFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: number;

  // Tech columns
  /** Bill creation date and time */
  created: Date;
  /** Bill last update date and time */
  updated: Date;
  /** Bill soft deletion date and time (`null` for not deleted bills) */
  deleted?: Date;

  // Data columns
  /** Currency rate for bill amount (`1` by default) */
  rate: number;
  /** Bill amount */
  amount: number;
  /** Bill Value Added Tax (VAT) amount */
  vatAmount: number;
  /** Document amount value without Value-Added Tax (VAT, rus. "НДС") */
  amountWithoutVat: number;
  /** Bill payment date */
  date: Date;
  /** Bill payment status from 1C ERP database */
  paymentStatus?: BillPaymentStatusEnum;
  /** Bill shipment status from 1C ERP database */
  shipmentStatus?: BillShipmentStatusEnum;

  // 1C ERP additional data columns
  /** Bill number in 1C ERP database (string in `'XXXX-XXXXXX'` format) */
  erp1cNumber?: string;
  /** Bill unique identifier in 1C ERP database (GUID) */
  erp1cRef?: string;
  /** Flag which marks bill from 1C ERP database */
  isFrom1c?: boolean;
  /** Posted bill flag */
  erp1cPosted?: boolean;

  // Mailing statistics
  /** The last date and time the bill file was sent via mailing lists */
  lastSentViaMailing?: Date;
  /**
   * Status of sending the bill file via mailings. Calculated automatically based on
   * the value of the {@link BillFindInterface.lastSentViaMailing} column.
   */
  mailingAttachmentStatus?: MailingAttachmentStatusEnum;

  // Analytical features relations
  /** The direction this bill is associated with */
  direction?: DirectionFindInterface;
  /** The unit this bill is associated with */
  unit?: UnitFindInterface;
  /** The filial this bill is associated with */
  filial?: FilialFindInterface;
  /** The deal this bill is associated with */
  deal?: DealFindInterface;

  // Other relations
  /** User who created bill */
  createdBy?: UserFindInterface;
  /** Company - bill sender */
  company?: CompanyFindInterface;
  /** Counterparty - bill receiver */
  contragent?: ContragentFindInterface;
  /** 1C ERP database - source of this bill */
  connection?: Connection1cFindInterface;
  /** Value Added Tax (VAT) rate for this bill */
  vat?: NdsFindInterface;
  /** The contract this bill associated with */
  contract?: ContractFindInterface;
  /** File attached to bill */
  file?: FileFindInterface;
  /** Bill's currency ("Russian Ruble" by default) */
  currency?: CurrencyFindInterface;
  /** Planned incomes bound to this bill */
  plannedIncomes?: PlannedIncomeFindInterface[];
}

/** Bill response body - DTO schema */
export const BillFindDto = SerdeDtoClass.create<BillFindInterface>(DocumentDtoKeysEnum.BILL_FIND_DTO, {
  id: {
    sortable: true,
    label: 'Номер',
    serializer: yup.number(),
  },
  // Tech columns
  created: {
    sortable: true,
    label: 'Создан',
    serializer: yup.date(),
  },
  updated: {
    label: 'Изменен',
    serializer: yup.date(),
  },
  deleted: {
    optional: true,
    label: 'Удален',
    serializer: yup.date(),
  },
  // Data columns
  rate: {
    label: 'Курс',
    serializer: yup.number(),
  },
  amount: {
    sortable: true,
    label: 'Сумма',
    serializer: yup.number(),
  },
  vatAmount: {
    sortable: true,
    label: 'Сумма НДС',
    serializer: yup.number(),
  },
  amountWithoutVat: {
    sortable: true,
    label: 'Сумма без НДС',
    hint: 'Сумма счета без НДС',
    serializer: yup.number(),
  },
  date: {
    sortable: true,
    label: 'Создан',
    serializer: yup.date(),
  },
  paymentStatus: {
    sortable: true,
    optional: true,
    label: 'Статус оплаты',
    serializer: yup
      .string()
      .oneOf(Object.values(BillPaymentStatusEnum)),
  },
  shipmentStatus: {
    sortable: true,
    optional: true,
    label: 'Статус отгрузки',
    serializer: yup
      .string()
      .oneOf(Object.values(BillShipmentStatusEnum)),
  },
  // 1C ERP additional data columns,
  erp1cNumber: {
    optional: true,
    sortable: true,
    label: 'Номер в 1С',
    serializer: yup.string(),
  },
  erp1cRef: {
    optional: true,
    label: 'Ссылочный номер в 1С',
    serializer: yup.string(),
  },
  isFrom1c: {
    optional: true,
    label: 'Счет из 1С?',
    serializer: yup.boolean(),
  },
  // Mailing statistics
  lastSentViaMailing: {
    optional: true,
    label: 'В последний раз отправлялся через рассылки',
    serializer: yup.date(),
  },
  mailingAttachmentStatus: {
    optional: true,
    label: 'Статус отправки через рассылки',
    serializer: yup
      .string()
      .oneOf(Object.values(MailingAttachmentStatusEnum)),
  },
  // Analytical features relations
  direction: {
    sortable: true,
    optional: true,
    label: 'БС',
    nested: DirectionFindDto,
    serializer: (_, nested) => nested,
  },
  unit: {
    sortable: true,
    optional: true,
    label: 'БЕ',
    nested: UnitFindDto,
    serializer: (_, nested) => nested,
  },
  filial: {
    sortable: true,
    optional: true,
    label: 'Филиал',
    nested: FilialFindDto,
    serializer: (_, nested) => nested,
  },
  deal: {
    sortable: true,
    optional: true,
    label: 'Сделка',
    nested: DealFindDto,
    serializer: (_, nested) => nested,
  },
  // Other relations
  createdBy: {
    optional: true,
    label: 'Автор',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  company: {
    sortable: true,
    optional: true,
    label: 'Компания',
    nested: CompanyFindDto,
    serializer: (_, nested) => nested,
  },
  contragent: {
    sortable: true,
    optional: true,
    label: 'Контрагент',
    nested: ContragentFindDto,
    serializer: (_, nested) => nested,
  },
  connection: {
    sortable: true,
    optional: true,
    label: 'База 1С',
    nested: Connection1cFindDto,
    serializer: (_, nested) => nested,
  },
  vat: {
    sortable: true,
    optional: true,
    label: 'Ставка НДС',
    nested: NdsFindDto,
    serializer: (_, nested) => nested,
  },
  contract: {
    sortable: true,
    optional: true,
    label: 'Договор',
    nested: ContractFindDto,
    serializer: (_, nested) => nested,
  },
  file: {
    optional: true,
    label: 'Файл',
    nested: FileFindDto,
    serializer: (_, nested) => nested,
  },
  currency: {
    sortable: true,
    optional: true,
    label: 'Договор',
    nested: CurrencyFindDto,
    serializer: (_, nested) => nested,
  },
  plannedIncomes: {
    optional: true,
    label: 'Планируемые поступления',
    nested: TaskDtoKeysEnum.PLANNED_INCOME_FIND_DTO,
    serializer: (_, nested) => yup.array(nested),
  },
});
